import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  useFeaturePrdForCanvas,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { PRDDesignSuggestion, PRDDesignSuggestions } from "./PRD/PRD";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { getContextFromFeaturePrd } from "./EngSpec/utils";
import { generateJsonStream } from "@/utils/ai/utils";
import {
  fixJson,
  getNewStoryboardPosition,
  getUnexpiredToken,
  logAxiosError,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
} from "@/utils/utils";
import { toast } from "sonner";
import _, { replace } from "lodash";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { v4 as uuidv4 } from "uuid";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import {
  LineConnectorAnnotation,
  NavTreeNode,
  Storyboard,
} from "@/utils/types";
import {
  ManualAssistantMessage,
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { Loader2 } from "lucide-react";
import { useTriggerGenerateSb } from "../Canvas/hooks/useTriggerGenerateSb";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import {
  ReactFramework,
  TEMPLATE_CONFIGS,
  TEMPLATE_PROJECT_PATHS,
} from "@/utils/templateProjects";
import { track } from "@/utils/events/tracker";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { useSession } from "@clerk/clerk-react";
import { XIcon } from "lucide-react";

const throttledEditSuggestions = _.throttle(
  async (
    supabaseClient: any,
    newSuggestions: any,
    canvasId: string,
    featurePrd: any,
    onDbSnapshot: any
  ) => {
    onDbSnapshot("featurePrds", {
      newItems: [{ ...featurePrd, design_suggestions: newSuggestions }],
    });

    await supabaseClient
      .from("feature_prd")
      .update({
        design_suggestions: newSuggestions,
      })
      .eq("canvas_id", canvasId);
  },
  1000
);

const DesignAgent = React.memo(() => {
  const canvasId = useCanvasId();

  const featurePrd = useFeaturePrdForCanvas();
  const { session } = useSession();

  const setStreamedDesignSuggestions = useAiContextStore(
    (state) => state.setStreamedDesignSuggestions
  );

  const generatingDesignSuggestions = useAiContextStore(
    (state) => state.generatingDesignSuggestions
  );
  const setGeneratingDesignSuggestions = useAiContextStore(
    (state) => state.setGeneratingDesignSuggestions
  );

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const { triggerGenerate } = useTriggerGenerateSb();

  const { supabaseClient } = useSupabase();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );
  const activeCanvas = useActiveCanvas();
  const activeCanvasRef = useRef(activeCanvas);
  useEffect(() => {
    activeCanvasRef.current = activeCanvas;
  }, [activeCanvas]);

  const generatingStoryboardIds = useAiContextStore(
    (state) => state.generatingStoryboardIds
  );

  const triggerGenerateWireframes = useAiContextStore(
    (state) => state.triggerGenerateWireframes
  );
  const setTriggerGenerateWireframes = useAiContextStore(
    (state) => state.setTriggerGenerateWireframes
  );

  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage
  );

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree
  );

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );

  const generateText = useProjectCreateStore((state) => state.generateText);
  const generateAttachedFiles = useProjectCreateStore(
    (state) => state.generateAttachedFiles
  );

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const [abortController, setAbortController] = useState(new AbortController());
  const [isDialogClosed, setIsDialogClosed] = useState(false);
  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);

  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  useEffect(() => {
    if (stopAiTrigger && abortController) {
      abortController.abort();
      // @ts-expect-error TS2345
      setAbortController(null);
    }
  }, [stopAiTrigger]);

  const layoutMode = useLayoutStore((state) => state.layoutMode);

  const project = useActiveProject();

  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);
  const gitStatusLoadingStatus = useSelector(
    (state: any) => state.gitStatus.status
  );

  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );

  const isChatLoading = useIsChatLoading();

  const {
    replaceMainStoryboard,
    loading: loadingUseMainStoryboard,
    mainStoryboardFilename,
  } = useMemo(() => {
    if (
      gitStatusLoadingStatus !== "succeeded" ||
      !activeCanvas ||
      projectLaunchStatus !== "succeeded"
    ) {
      return {
        replaceMainStoryboard: false,
        loading: true,
        mainStoryboardFilename: null,
      };
    }

    // Changes to these files (without changes to other files) are not valid for not replacing the main storyboard
    const notImportantFiles = [
      "package-lock.json",
      "pnpm-lock.yaml",
      "pnpm-workspace.yaml",
      "bun.lockb",
      "bun.lockb",

      // In case devtools gets updated
      "package.json",
    ];
    const notImportantFolders = ["node_modules", "dist", "build", "public"];
    const importantFilesChanged = gitStatus?.status?.files?.some(
      (file: any) =>
        !notImportantFiles.some((notImportantFile) =>
          file?.path?.endsWith(notImportantFile)
        ) &&
        !notImportantFolders.some(
          (notImportantFolder) =>
            file?.path?.includes(`/${notImportantFolder}/`) ||
            file?.path?.startsWith(`${notImportantFolder}/`)
        )
    );

    // If there are any git changes, do not replace the main storyboard
    if (!project?.repoPath || importantFilesChanged) {
      return {
        replaceMainStoryboard: false,
        loading: false,
        mainStoryboardFilename: null,
      };
    }

    // Using the DOM tree for the main application storyboad, find the file that needs to be modified
    const applicationSbs = activeCanvas.storyboards?.filter(
      (sb) => sb.type === "APPLICATION"
    );
    const firstSbNavTree = storyboardToNavTree[applicationSbs?.[0]?.id];

    if (!applicationSbs) {
      return {
        replaceMainStoryboard: false,
        loading: false,
        mainStoryboardFilename: null,
      };
    }

    if (!firstSbNavTree) {
      // Still loading the storyboard
      return {
        replaceMainStoryboard: false,
        loading: true,
        mainStoryboardFilename: null,
      };
    }

    let mainStoryboardFilename = null;

    const filesSeen = new Set();
    const findMainStoryboardFile = (navTree: NavTreeNode) => {
      if (!navTree) {
        return;
      }

      const treeElementForNode =
        treeElementLookup[navTree.tempoElement.codebaseId];

      // This is the single element in the home page that should exist, if valid use this filename
      if (
        (navTree?.elementTagName?.toLowerCase() === "div" ||
          navTree?.elementTagName?.toLowerCase() === "view") &&
        !navTree?.children?.length
      ) {
        const valid =
          treeElementForNode?.filename &&
          !filesSeen.has(treeElementForNode.filename);
        if (valid) {
          // @ts-expect-error TS2322
          mainStoryboardFilename = treeElementForNode.filename;
        }

        return;
      }

      if (treeElementForNode?.filename && !navTree.isComponent) {
        filesSeen.add(treeElementForNode.filename);
      }

      navTree.children?.forEach(findMainStoryboardFile);
    };

    findMainStoryboardFile(firstSbNavTree);

    return {
      replaceMainStoryboard: Boolean(
        TEMPLATE_PROJECT_PATHS.includes(project.repoPath?.toLowerCase()) &&
          applicationSbs.length === 1 &&
          mainStoryboardFilename
      ),
      loading: false,
      mainStoryboardFilename,
    };
  }, [project, gitStatusLoadingStatus, activeCanvas, storyboardToNavTree]);

  const prevGeneratingSuggestion = usePrevious(generatingDesignSuggestions);

  const designSuggestions =
    featurePrd?.design_suggestions as PRDDesignSuggestions;

  useEffect(() => {
    if (
      !generatingDesignSuggestions &&
      prevGeneratingSuggestion &&
      !generatingStoryboardIds?.size &&
      designSuggestions?.pageOrComponent
    ) {
      triggerGenerate();
    }
  }, [generatingDesignSuggestions]);

  const initializeStoryboard = useInitializeStoryboard();

  const generateSuggestions = async () => {
    if (!supabaseClient) {
      console.error("Supabase client not available");
      return;
    }

    if (canvasComponentKey) {
      return;
    }

    setNewLayout(LeftTab.AIChat);

    const messageText =
      "Creating wireframes based on the PRD and mermaid diagram. Tip: if you delete all the generated wireframes you'll be able to click to regenerate.";
    const progressMessage: ManualAssistantMessage = {
      id: uuidv4().toString(),
      text: messageText,
      progressItems: [
        {
          title: "Wireframes Spec",
          loading: true,
        },
      ],
    };
    setTempAssistantMessage(progressMessage);

    setGeneratingDesignSuggestions(true);

    const generatedIds = [];
    const generatedIdToStoryboard = {};

    // If new storyboard is created, scroll to it
    let scrollToStoryboardId: string | null = null;

    const createAndArrangeStoryboards = async (
      designSuggestions: PRDDesignSuggestions,
      pathToComponent: Record<string, PRDDesignSuggestion> = {}
    ) => {
      let totalWidth = 0,
        totalHeight = 0,
        vGap = 250,
        hGap = 150;

      const treeNodeIdToDimensions = {};

      const pageOrComp = designSuggestions.pageOrComponent;

      const processComponent = (component: PRDDesignSuggestion) => {
        let sizeComponent = component;
        if (component.previouslyDefined) {
          sizeComponent = pathToComponent[component.path];
        }

        if (!sizeComponent?.estimated_wireframe_dimensions) {
          treeNodeIdToDimensions[component.id] = {
            width: 10,
            height: 10,
          };
          return { width: 10, height: 10 };
        }

        let height = sizeComponent.estimated_wireframe_dimensions.height || 0;
        let width = sizeComponent.estimated_wireframe_dimensions.width || 0;

        if (!component.components?.length) {
          treeNodeIdToDimensions[component.id] = { height, width };
          return { width, height };
        }

        let maxChildrenHeight = 0,
          totalChildrenWidth = -hGap;
        component.components?.forEach((component) => {
          const { height: childHeight, width: childWidth } =
            processComponent(component);
          maxChildrenHeight = Math.max(maxChildrenHeight, childHeight);
          totalChildrenWidth += childWidth + hGap;
        });

        width = Math.max(width, totalChildrenWidth);
        height = height + vGap + maxChildrenHeight;

        treeNodeIdToDimensions[component.id] = { height, width };

        return { height, width };
      };

      const { height: pageTreeHeight, width: pageTreeWidth } =
        processComponent(pageOrComp);
      treeNodeIdToDimensions[pageOrComp.id] = {
        height: pageTreeHeight,
        width: pageTreeWidth,
      };

      const createOrUpdateStoryboardForId = (
        component: PRDDesignSuggestion,
        x: number,
        y: number,
        width: number,
        height: number,
        topOfTree: boolean
      ) => {
        // Use the main application storyboard instead
        if (topOfTree && replaceMainStoryboard) {
          generatedIdToStoryboard[component.id] = {
            // @ts-expect-error TS18047
            ...activeCanvas.storyboards.find((sb) => sb.type === "APPLICATION"),

            // Change the ID to match
            id: component.id,
            designSuggestionId: component.id,
            x,
            y,
            width,
            height,
          };
          return;
        }

        let placeholderComponent = component;
        if (component?.previouslyDefined) {
          placeholderComponent = pathToComponent[component.path];
        }

        // Dimensions have been streamed in and are fixed
        // Explicit key used by the backend
        if (!placeholderComponent?.estimated_wireframe_dimensions?.fin) {
          return;
        }

        const id = component.id;
        if (generatedIdToStoryboard[id]) {
          generatedIdToStoryboard[id].x = x;
          generatedIdToStoryboard[id].y = y;
          generatedIdToStoryboard[id].width = width;
          generatedIdToStoryboard[id].height = height;
        } else {
          // @ts-expect-error TS2532
          const fileName = placeholderComponent?.path
            ?.split("/")
            .pop()
            .split(".")[0];

          const newStoryboard = initializeStoryboard(
            x,
            y,
            width,
            height,
            id,
            placeholderComponent.type === "PAGE"
              ? `Route: ${fileName}`
              : fileName,
            id,
            true,
            placeholderComponent?.path
          );

          scrollToStoryboardId = id;

          generatedIdToStoryboard[id] = newStoryboard;
        }
      };

      // Once the tree position is established, set the positions of the storyboards
      const x =
        totalWidth +
        pageTreeWidth / 2 -
        (pageOrComp?.estimated_wireframe_dimensions?.width || 100) / 2;
      const y = 0;
      createOrUpdateStoryboardForId(
        pageOrComp,
        x,
        y,
        pageOrComp?.estimated_wireframe_dimensions?.width || 100,
        pageOrComp?.estimated_wireframe_dimensions?.height || 100,
        true
      );

      const positionChildren = (
        comp: PRDDesignSuggestion,
        x: number,
        y: number
      ) => {
        if (!comp?.components?.length) {
          return;
        }

        // @ts-expect-error TS18048
        const childrenY = y + vGap + comp.estimated_wireframe_dimensions.height;

        const totalChildrenWidth = comp.components?.reduce((acc, child) => {
          return acc + treeNodeIdToDimensions[child.id].width;
        }, 0);

        let childX =
          // @ts-expect-error TS18048
          comp.estimated_wireframe_dimensions.width > totalChildrenWidth
            ? x +
              // @ts-expect-error TS18048
              (comp.estimated_wireframe_dimensions.width - totalChildrenWidth) /
                2
            : x;

        comp.components?.forEach((child) => {
          let sizeComponent = child;
          if (sizeComponent?.previouslyDefined) {
            sizeComponent = pathToComponent[child.path];
          }

          createOrUpdateStoryboardForId(
            child,
            childX +
              treeNodeIdToDimensions[child.id].width / 2 -
              (sizeComponent?.estimated_wireframe_dimensions?.width || 10) / 2,
            childrenY,
            sizeComponent?.estimated_wireframe_dimensions?.width || 10,
            sizeComponent?.estimated_wireframe_dimensions?.height || 10,
            false
          );

          positionChildren(child, childX, childrenY);

          childX += treeNodeIdToDimensions[child.id].width + hGap;
        });
      };

      positionChildren(pageOrComp, totalWidth, 0);

      totalWidth += pageTreeWidth + hGap * 2;
      totalHeight = Math.max(totalHeight, pageTreeHeight);

      // Finally update the canvas with the storyboards
      // All the storyboards should be shifted based on where they can fit in the canvas
      const { x: shiftX, y: shiftY } = getNewStoryboardPosition(
        totalWidth,
        totalHeight,
        // @ts-expect-error TS18047
        activeCanvasRef.current.storyboards.filter(
          (sb) =>
            !generatedIdToStoryboard[sb.id] &&
            // Either we are not replacing the main storyboard (so keep it), or it is not the application storyboard
            (!replaceMainStoryboard || sb.type !== "APPLICATION")
        )
      );

      // @ts-expect-error TS18047
      let newStoryboards = activeCanvasRef.current.storyboards.filter(
        (sb) => !generatedIdToStoryboard[sb.id]
      );

      // Remove the application storyboard if it exists (it will be re-added from generatedIdToStoryboard)
      if (replaceMainStoryboard) {
        newStoryboards = newStoryboards.filter(
          (sb) => sb.type !== "APPLICATION"
        );
      }

      newStoryboards = newStoryboards.concat(
        Object.values(generatedIdToStoryboard).map((storyboard: Storyboard) => {
          let storyboardToReturn = {
            ...storyboard,
            x: storyboard.x + shiftX,
            y: storyboard.y + shiftY,
          };

          if (scrollToStoryboardId === storyboard.id) {
            storyboardToReturn.scrollToStoryboard = true;
          }

          return storyboardToReturn;
        })
      );

      // Now create the annotations
      const annotations: LineConnectorAnnotation[] = [];
      const createAnnotation = (
        parent: PRDDesignSuggestion,
        component: PRDDesignSuggestion
      ) => {
        if (parent) {
          annotations.push({
            id: `${parent.id}_${component.id}`,
            type: "LINE_CONNECTOR",
            fromStoryboardId: parent.id,
            toStoryboardId: component.id,
            fromPosition: "BOTTOM",
            toPosition: "TOP",
          });
        }

        component.components?.forEach((child) => {
          createAnnotation(component, child);
        });
      };
      // @ts-expect-error TS2345
      createAnnotation(null, pageOrComp);

      // @ts-expect-error TS18047
      let newAnnotations = (activeCanvasRef.current.annotations || []).filter(
        (annotation) => {
          return !annotations.find(
            (newAnnotation) => newAnnotation.id === annotation.id
          );
        }
      );
      newAnnotations = newAnnotations.concat(annotations);

      await updateCanvasStoryboards(
        supabaseClient,
        dispatch,
        newStoryboards,
        // @ts-expect-error TS2345
        activeCanvasRef.current,
        null,
        newAnnotations
      );
    };

    /**
     * Update based on the current response format returned from the vellum workflow
     */
    const updateSuggestions = async (
      response: PRDDesignSuggestions,
      finalValue: boolean
    ) => {
      let idNum = 0;

      let createNewStoryboard = false;

      const pathToComponent = {};

      const processSuggestion = async (
        suggestion: PRDDesignSuggestion,
        topOfTree: boolean
      ) => {
        let id = generatedIds[idNum];
        if (!id) {
          // @ts-expect-error TS2345
          generatedIds.push(uuidv4().toString());
          id = generatedIds[idNum];
        }

        idNum++;

        // Dimensions have been streamed in and are fixed - fin is explicitly used by the AI
        // to indicate that it finished giving the dimensions. Check the system prompt
        if (
          suggestion?.estimated_wireframe_dimensions?.fin &&
          !generatedIdToStoryboard[id]
        ) {
          createNewStoryboard = true;
        }

        if (suggestion?.path && !suggestion?.previouslyDefined) {
          pathToComponent[suggestion.path] = suggestion;
        }

        if (topOfTree && replaceMainStoryboard) {
          // Edit the component to change the path as well (relative to base of project)
          // @ts-expect-error TS18047
          suggestion.path = mainStoryboardFilename.split(canvasId + "/")[1];
        }

        return {
          ...suggestion,
          id,
          components: await Promise.all(
            (suggestion.components || []).map(
              (comp) => processSuggestion(comp, false),
              false
            )
          ),
        };
      };

      const processedResponse = {
        pageOrComponent: response.pageOrComponent
          ? await processSuggestion(response.pageOrComponent, true)
          : null,
      };

      if (createNewStoryboard || finalValue) {
        await createAndArrangeStoryboards(processedResponse, pathToComponent);
      }

      setStreamedDesignSuggestions(processedResponse);

      throttledEditSuggestions(
        supabaseClient,
        processedResponse,
        canvasId,
        featurePrd,
        onDbSnapshot
      );
    };

    const supabaseJWT = await getUnexpiredToken(session);

    try {
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/ai/vercel-ai/aiWireframesGenerateSpec`;

      const { contextWithImages, userSelectionContext } =
        getContextFromFeaturePrd(featurePrd);

      const generateImages =
        generateAttachedFiles && generateAttachedFiles.length > 0
          ? generateAttachedFiles.map((file) => ({
              url: file.url,
              name: file.name || "Uploaded file",
              type: file.type || "image",
              mimeType: file.mimeType,
              context: "Image provided as part of the original prompt",
            }))
          : [];

      const allVisualContext = [...contextWithImages, ...generateImages];

      const body = {
        feature_title: featurePrd?.title || "",
        feature_prd: featurePrd?.prd || "",
        visual_context: allVisualContext,
        user_selection_context: JSON.stringify(userSelectionContext),
        mermaid_diagram: featurePrd?.mermaid_diagram || "",
        empty_project: replaceMainStoryboard,

        original_user_prompt: generateText,
      };

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: newAbortController.signal,
      });

      let res = null;
      let partialResult = "";
      for await (let chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          const parsedChunk = JSON.parse(chunk.data);

          if (parsedChunk.type === "text-delta") {
            partialResult += parsedChunk.chunk || "";
          }

          await updateSuggestions(fixJson(partialResult).fixedObj, false);
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }

      // @ts-expect-error TS18047
      const finalText = res.data.steps[0]?.text;
      const fixedJson = fixJson(finalText).fixedObj;
      // @ts-expect-error TS2345
      setAbortController(null);
      // @ts-expect-error TS2345
      setStreamedDesignSuggestions(null);
      await updateSuggestions(fixedJson, true);
      setGeneratingDesignSuggestions(false);

      // @ts-expect-error TS18048
      progressMessage.progressItems[0].succeeded = true;
      setTempAssistantMessage({ ...progressMessage });
    } catch (err) {
      // @ts-expect-error TS2345
      setStreamedDesignSuggestions(null);
      // @ts-expect-error TS2345
      setAbortController(null);
      logAxiosError("Error generating wireframe spec", err);
      if (err.name !== "AbortError") {
        toast.error(`Error generating wireframe spec: ` + err.message);
      }
      setGeneratingDesignSuggestions(false);

      // @ts-expect-error TS18048
      progressMessage.progressItems[0].failed = true;
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage(progressMessage);
    } finally {
      refreshPlanInfo(session);
    }
  };

  useEffect(() => {
    if (loadingUseMainStoryboard) {
      return;
    }

    if (canvasComponentKey || projectLaunchStatus !== "succeeded") return;

    if (!featurePrd?.prd && !featurePrd?.mermaid_diagram) {
      setTriggerGenerateWireframes(false);
      return;
    }

    if (triggerGenerateWireframes) {
      setTriggerGenerateWireframes(false);
      generateSuggestions();
    }
  }, [
    loadingUseMainStoryboard,
    triggerGenerateWireframes,
    projectLaunchStatus,
  ]);

  const dispatch = useDispatch();

  const disableActions =
    isChatLoading || generatingDesignSuggestions || triggerGenerateWireframes;

  const showButton =
    !isDialogClosed &&
    (featurePrd?.prd || featurePrd?.mermaid_diagram) &&
    projectLaunchStatus === "succeeded" &&
    !featurePrd?.design_suggestions &&
    (project?.repoPath === TEMPLATE_CONFIGS.default.default?.repoPath ||
      project?.repoPath ===
        TEMPLATE_CONFIGS.default[ReactFramework.NEXT_JS]?.repoPath ||
      project?.repoPath ===
        TEMPLATE_CONFIGS.default[ReactFramework.EXPO]?.repoPath) &&
    !gitStatus?.status?.files?.length;

  const showLoadingGenerate =
    generatingDesignSuggestions || triggerGenerateWireframes;

  return (
    <div
      className={cn(
        "flex flex-col gap-4 p-2 rounded-md bg-secondary-n-1000 max-w-[286px] pointer-events-auto relative",
        !showButton && "hidden",
        featurePrd?.design_suggestions && "!p-0 shadow"
      )}
    >
      {/* Close button */}
      <button
        onClick={() => {
          // Hide the dialog by setting the closed state
          setIsDialogClosed(true);
        }}
        className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors z-10"
        aria-label="Close dialog"
      >
        <XIcon className="w-4 h-4" />
      </button>

      <div
        className={cn(
          "flex flex-col px-2 gap-1.5",
          featurePrd?.design_suggestions && "hidden"
        )}
      >
        <div className="text-sm font-medium">Generate Designs from PRD</div>
        <div className="text-xs text-secondary-n-300">
          Tempo will analyze your PRD to generate designs.
        </div>
      </div>
      <Button
        onClick={() => {
          track("AI_PROMPT_REQUESTED", { type: "DESIGN_SUGGESTIONS" });
          generateSuggestions();
        }}
        variant={featurePrd?.design_suggestions ? "outline" : "primary"}
        size="sm"
        className={cn("text-xs h-7", canvasComponentKey && "hidden")}
        disabled={disableActions}
      >
        <div
          className={cn("animate-spin mr-1", !showLoadingGenerate && "hidden")}
        >
          <Loader2 className="h-4 w-4" />
        </div>
        <span>
          {showLoadingGenerate && loadingUseMainStoryboard
            ? "Waiting for Storyboard to load"
            : "Generate Wireframes"}
        </span>
      </Button>
    </div>
  );
});

export default DesignAgent;
